import {
  type PeopleAnalyticsObjectKind,
  type VMSMultiPeopleTrackingCameras,
  type VMSPeopleTrackingCamera,
  type VMSSinglePeopleTrackingCamera,
} from '@solink/vd-data-layer/api-clients/vms/vmsTypes';
import { type CallhomeCamera, type DeviceEntity } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { toast } from '@solinkcorp/solink-components';
import { VmsApi } from 'clients';
import { type LineCrossEventsLine } from 'components/VideoPlayer/components/AI/LineCross/types';
import useSWR from 'swr';
import useVisionAnalyticsSystemRequirement from './useVisionaAnalyticsConfigRequirement';

export type PeopleTrackingCamera = ReturnType<typeof usePeopleTrackingCamera>;
export type EnabledCamera =
  | {
      enabled: boolean;
      vmsCamId: string;
      streamId: string;
      chCamId: string | undefined;
      name: string;
      // LEGACY - Use eventLines instead
      crossingEventLine?: LineCrossEventsLine;
      eventLines: LineCrossEventsLine[];
      objectKinds: PeopleAnalyticsObjectKind[];
    }
  | undefined;

export function isSinglePeopleTrackingCameraConfig(
  data: VMSPeopleTrackingCamera,
): data is VMSSinglePeopleTrackingCamera {
  return !!data.cam_id;
}

const MAX_DEFAULT_MULTI_CAMERA_TRACKING = 3; // required to support interlink versions < 130 with no max_instance connect settings override
const INTERLINK_VERSION_WITH_MULTIPLE_CAMERAS_ENABLED = 122;
const STATUSES_FOR_TPU_ACTIVE = ['OK', 'OK_TO_UNKNOWN_1', 'OK_TO_UNKNOWN_2'];

const usePeopleTrackingCamera = ({
  deviceUrl,
  locationCameras,
  locationVersion,
  maxNumberOfCamerasOverride,
  tpuStatus,
  isVisionAnalyticsEnabled = false,
  deviceName = '',
}: {
  deviceUrl: string | null;
  locationCameras: CallhomeCamera[] | undefined;
  locationVersion?: DeviceEntity['currentSoftwareMajorVersion'] | undefined;
  maxNumberOfCamerasOverride?: number | undefined;
  tpuStatus?: DeviceEntity['tpuStatus'] | undefined;
  isVisionAnalyticsEnabled?: boolean;
  deviceName?: string;
}): {
  cameras?: EnabledCamera[];
  maxCameras?: number;
  deviceUrl: string | null;
  revalidateCache: () => void;
  optimisticallyUpdateCache: (camera: VMSSinglePeopleTrackingCamera) => void;
  revertCache: (prevData: VMSPeopleTrackingCamera | undefined) => void;
  rawData: VMSPeopleTrackingCamera | undefined;
  toolTipMsg?: string;
} => {
  const user = useUserSession();
  const systemConfigurationSettings = useVisionAnalyticsSystemRequirement({
    deviceUrl,
    currentSoftwareMajorVersion: locationVersion,
    isVisionAnalyticsEnabled,
    deviceName,
  });

  const supportsMultiCamera =
    locationVersion &&
    locationVersion >= INTERLINK_VERSION_WITH_MULTIPLE_CAMERAS_ENABLED &&
    tpuStatus &&
    STATUSES_FOR_TPU_ACTIVE.includes(tpuStatus);

  const getMaxCameras = () => {
    if (!supportsMultiCamera) return 1;

    // Priority: Connect override > system config > legacy default
    if (maxNumberOfCamerasOverride) return maxNumberOfCamerasOverride;
    if (systemConfigurationSettings.maxCameraTrackers)
      return systemConfigurationSettings.maxCameraTrackers;
    return MAX_DEFAULT_MULTI_CAMERA_TRACKING;
  };

  const maxCameras = getMaxCameras();

  const { data, mutate } = useSWR(
    deviceUrl && isVisionAnalyticsEnabled ? ['peopleTrackingCamera', deviceUrl] : null,
    async ([, deviceUrl]) => {
      const res: VMSPeopleTrackingCamera = await VmsApi(
        deviceUrl,
        false,
        user.accessToken,
      ).getPeopleTrackingCameraConfig(maxCameras !== 1 ? true : false);
      return res;
    },
    {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
      refreshInterval: 60000,
    },
  );

  const optimisticallyUpdateCache = (camera: VMSSinglePeopleTrackingCamera) => {
    if (maxCameras === 1) {
      mutate(camera.enabled ? camera : undefined, false);
    } else {
      const newData = { ...(data ?? {}) } as VMSMultiPeopleTrackingCameras;

      if (!camera.enabled) {
        delete newData[camera.cam_id];
      } else {
        newData[camera.cam_id] = camera;
      }
      mutate(newData, false);
    }
  };

  const revertCache = (prevData: VMSPeopleTrackingCamera | undefined) => {
    mutate(prevData, false);
  };

  const revalidateCache = () => {
    mutate();
  };

  if (!data || Object.values(data).every((x) => !x)) {
    return {
      deviceUrl,
      maxCameras,
      revalidateCache: mutate,
      optimisticallyUpdateCache,
      revertCache,
      rawData: data,
    };
  }

  if (!locationCameras) {
    toast.error('No location cameras found in usePeopleTrackingCamera hook');
    return {
      deviceUrl,
      maxCameras,
      revalidateCache: mutate,
      optimisticallyUpdateCache,
      revertCache,
      rawData: data,
    };
  }

  if (isSinglePeopleTrackingCameraConfig(data) && !data?.enabled) {
    return {
      deviceUrl,
      maxCameras,
      revalidateCache: mutate,
      optimisticallyUpdateCache,
      revertCache,
      rawData: data,
    };
  }

  let cameras: EnabledCamera[];
  let camera: CallhomeCamera | undefined;

  // Single camera format, no nesting
  if (isSinglePeopleTrackingCameraConfig(data)) {
    camera = locationCameras.find((camera) => camera.cameraId === data.cam_id);
    cameras = [
      {
        enabled: true,
        vmsCamId: data.cam_id,
        streamId: data.stream_id,
        chCamId: camera?.id,
        name: camera?.name ?? 'Unknown Camera',
        eventLines: data.event_lines ?? [],
        objectKinds: data.object_kinds,
      },
    ];
  } else {
    // Multi-camera format, nested by camId
    cameras = Object.entries(data).map((entry) => {
      camera = locationCameras.find((camera) => camera.cameraId === entry[0]);
      return {
        enabled: entry[1].enabled,
        vmsCamId: entry[0],
        streamId: entry[1].stream_id,
        chCamId: camera?.id,
        name: camera?.name ?? 'Unknown Camera',
        eventLines: entry[1]?.event_lines ?? [],
        objectKinds: entry[1].object_kinds,
      };
    });
  }

  return {
    cameras,
    maxCameras,
    deviceUrl,
    revalidateCache,
    optimisticallyUpdateCache,
    revertCache,
    rawData: data,
    toolTipMsg: systemConfigurationSettings.toolTipMsg,
  };
};

export default usePeopleTrackingCamera;
